import {
    Component,
    ElementRef,
    input,
    model,
    OnInit,
    output,
    Renderer2,
    RendererFactory2,
    ViewChild,
    AfterContentInit,
    ContentChild,
} from '@angular/core';
import { Button } from '../button/button.component';
import { HttpClient, HttpEvent, HttpEventType } from '@angular/common/http';
import { fromEvent } from 'rxjs';

/**
 * Evento que é lançado quando um arquivo é selecionado pelo usuário.
 *
 */
export type UploadFilesSelectedEvent = {
    files: FileList | null;
};

/**
 * Evento que é lançado quando o upload de um arquivo é bem sucedido.
 *
 */
export type UploadSuccessEvent = {
    event: HttpEvent<any>;
    file: File;
};

/**
 * Evento que é lançado quando o upload de um arquivo falha.
 *
 */
export type UploadFailedEvent = {
    file: File;
    error: any;
};

/**
 * Evento que é lançado durante o upload de um arquivo para informar o progresso
 * do Upload em andamento.
 *
 */
export type UploadProgressEvent = {
    file: File;
    loaded: number | null;
    total: number | undefined;
    progress: number;
};

/**
 * Evento lançado quando um ou mais arquivos entregues (seleção ou
 * drag-and-drop) são descartados por violarem uma restrição do componente
 * — atualmente, apenas o limite de {@link UploadFile.multiple}.
 *
 */
export type UploadFilesRejectedEvent = {
    /** Quantidade de arquivos descartados. */
    ignoredCount: number;
};

/**
 * Upload de arquivos com suporte a drag-and-drop e envio via HTTP.
 *
 * Pode enviar automaticamente ({@link auto}) ao selecionar, ou aguardar o
 * comando do consumidor. Emite eventos de seleção ({@link filesSelected}),
 * progresso ({@link progress}), sucesso ({@link success}) e falha ({@link failed}).
 */
@Component({
    selector: 'ai-upload-file',
    standalone: true,
    templateUrl: './upload-file.component.html',
    styleUrls: ['./upload-file.component.scss'],
})
export class UploadFile implements OnInit, AfterContentInit {
    @ViewChild('fileupload')
    private fileupload!: ElementRef;
    private renderer: Renderer2;

    @ContentChild(Button)
    private button!: Button;

    /** URL de destino do upload. */
    public url = input<string | undefined>();
    /** Habilita a zona de drop em toda a janela. */
    public dragAndDropWindowZone = input<boolean>(false);
    /** Habilita a zona de drop no elemento pai. */
    public dragAndDropParentZone = input<boolean>(false);
    /** Elemento específico usado como zona de drop. */
    public dragAndDropZone = input<HTMLElement | undefined>(undefined);
    /** Tamanho máximo por arquivo (0 = sem limite). */
    public maxFileSize = input<number>(0);
    /** Tipos de arquivo aceitos (atributo `accept`). */
    public accept = input<string>();
    /** Rótulo do botão de upload. */
    public label = input<string>();
    /** Envia automaticamente os arquivos ao selecioná-los. */
    public auto = input<boolean>(false);
    /** Permite selecionar múltiplos arquivos. */
    public multiple = input<boolean>(false);
    /** Emite quando um upload é concluído com sucesso. */
    public success = output<UploadSuccessEvent>();
    /** Emite quando um upload falha. */
    public failed = output<UploadFailedEvent>();
    /** Emite o progresso do upload em andamento. */
    public progress = output<UploadProgressEvent>();
    public filesSelected = output<UploadFilesSelectedEvent>();
    /** Emite quando arquivos entregues via drag-and-drop são descartados (ex.: mais de um com {@link multiple} desabilitado). */
    public filesRejected = output<UploadFilesRejectedEvent>();
    public files = model<FileList | null>(null);
    /**
     * Indica, em tempo real, se há um arraste de arquivo em andamento sobre a
     * zona de drop configurada ({@link dragAndDropZone}, {@link dragAndDropParentZone}
     * ou {@link dragAndDropWindowZone}). Bindável nos dois sentidos (`[(isDraggingOver)]`)
     * para que o consumidor exiba um destaque visual durante o arraste.
     */
    public isDraggingOver = model<boolean>(false);

    /** Contador de `dragenter`/`dragleave` não compensados, usado para evitar
     * que o estado de arraste "pisque" ao passar por elementos filhos da zona de drop. */
    private dragEnterCount = 0;

    constructor(
        private httpClient: HttpClient,
        private rendererFactory2: RendererFactory2,
        private elementRef: ElementRef,
    ) {
        this.renderer = this.rendererFactory2.createRenderer(null, null);
    }

    public ngOnInit(): void {
        this.handleDragAndDropZones();
    }

    public ngAfterContentInit(): void {
        if (this.button) {
            this.button.click.subscribe((event: Event) => {
                this.onButtonClick(event);
            });
        }
    }

    /**
     * Trata as possíveis áreas de Drag and Drop, tratando cada uma conforme a necessidade.
     *
     */
    private handleDragAndDropZones(): void {
        // Drag and Drop da janela do browser tem prioridade sobre os demais tipos.
        if (this.dragAndDropWindowZone()) {
            this.renderer.listen('window', 'dragenter', (event: Event) => {
                event.preventDefault();
                event.stopPropagation();
                this.dragEnterCount++;
                this.isDraggingOver.set(true);
            });

            this.renderer.listen('window', 'dragover', (event: Event) => {
                event.preventDefault();
                event.stopPropagation();
            });

            this.renderer.listen('window', 'dragleave', (event: Event) => {
                event.preventDefault();
                event.stopPropagation();
                this.decrementDragCounter();
            });

            this.renderer.listen('window', 'drop', (event: any) => {
                event.preventDefault();
                event.stopPropagation();

                this.resetDragCounter();
                this.handleIncomingFiles(event.dataTransfer?.files ?? null);
            });
        } else {
            let element: HTMLElement | null = null;

            if (
                this.dragAndDropParentZone() &&
                this.elementRef?.nativeElement?.parentElement
            ) {
                element = this.elementRef.nativeElement.parentElement;
            } else if (this.dragAndDropZone()) {
                element = this.dragAndDropZone()!;
            }

            if (element) {
                fromEvent(element, 'dragenter').subscribe((event: any) => {
                    event.preventDefault();
                    event.stopPropagation();
                    this.dragEnterCount++;
                    this.isDraggingOver.set(true);
                });

                fromEvent(element, 'dragover').subscribe((event: any) => {
                    event.preventDefault();
                    event.stopPropagation();
                });

                fromEvent(element, 'dragleave').subscribe((event: any) => {
                    event.preventDefault();
                    event.stopPropagation();
                    this.decrementDragCounter();
                });

                fromEvent(element, 'drop').subscribe((event: any) => {
                    event.preventDefault();
                    event.stopPropagation();

                    this.resetDragCounter();
                    this.handleIncomingFiles(event.dataTransfer?.files ?? null);
                });
            }
        }
    }

    /**
     * Decrementa o contador de `dragenter`/`dragleave` não compensados e só
     * desliga o estado de arraste quando o contador zera — evita que o
     * destaque visual "pisque" ao sobrevoar elementos filhos da zona de drop.
     */
    private decrementDragCounter(): void {
        this.dragEnterCount = Math.max(0, this.dragEnterCount - 1);

        if (this.dragEnterCount === 0) {
            this.isDraggingOver.set(false);
        }
    }

    /**
     * Zera o contador de arraste e desliga o destaque visual — chamado ao
     * soltar os arquivos, já que nem sempre o `dragleave` é disparado antes do `drop`.
     */
    private resetDragCounter(): void {
        this.dragEnterCount = 0;
        this.isDraggingOver.set(false);
    }

    /**
     * Trata os arquivos recebidos via drag-and-drop, aplicando a mesma
     * restrição de quantidade do `<input>` nativo: quando {@link multiple}
     * está desabilitado, apenas o primeiro arquivo solto é aceito e os
     * demais são descartados (emitindo {@link filesRejected} para que o
     * consumidor possa avisar o usuário).
     *
     * @param fileList Arquivos entregues pelo evento `drop`.
     */
    private handleIncomingFiles(fileList: FileList | null): void {
        if (!fileList || fileList.length === 0) {
            return;
        }

        let filesToUse = fileList;

        if (!this.multiple() && fileList.length > 1) {
            const singleFileTransfer = new DataTransfer();
            singleFileTransfer.items.add(fileList[0]);
            filesToUse = singleFileTransfer.files;

            this.filesRejected.emit({ ignoredCount: fileList.length - 1 });
        }

        this.files.set(filesToUse);
        this.filesSelected.emit({ files: filesToUse });

        if (this.auto()) {
            this.sendFiles();
        }
    }

    /**
     * Trata o evento lançado pelo Input de arquivos quando um arquivo ou mais arquivos são selecionados.
     *
     * @param event Evento de seleção do arquivo, contendo os arquivos selecionados.
     */
    protected onFileSelected(event: Event): void {
        const target = event.target as HTMLInputElement;
        this.files.set(target.files);
        this.filesSelected.emit({ files: target.files });

        if (this.auto()) {
            this.sendFiles();
        }
    }

    /**
     * Trata o evento de clique no botão de upload contido no template.
     *
     * Chama o evento click() do input de upload de arquivos para que
     * seja feita a seleção dos arquivos.
     *
     */
    protected onButtonClick(_: Event): void {
        this.fileupload.nativeElement.click();
    }

    /**
     * Envia os arquivos que foram selecionados, caso existam.
     * Caso nenhum arquivo tenha sido selecionado, nada será feito.
     *
     */
    public sendFiles(): void {
        if (this.url() && this.files() && this.files()!.length > 0) {
            for (let i = 0; i < this.files()!.length; i++) {
                const file = this.files()![i];

                this.httpClient
                    .post(this.url()!, file, {
                        observe: 'events',
                        reportProgress: true,
                    })
                    .subscribe({
                        next: (event: HttpEvent<any>) => {
                            if (event.type === HttpEventType.UploadProgress) {
                                const progress = Math.round(
                                    100 * (event.loaded / (event.total ?? 0)),
                                );
                                this.progress.emit({
                                    file: file,
                                    progress: progress,
                                    loaded: event.loaded,
                                    total: event.total,
                                });
                            } else if (event.type === HttpEventType.Response) {
                                this.success.emit({ file: file, event: event });
                            }
                        },
                        error: (error: any) => {
                            this.failed.emit({ file: file, error: error });
                        },
                    });
            }

            this.files.set(null);
        }
    }
}
